// src/pages/markets/board/card/EventImageLink.tsx
//
// The image is the card's only exit to the event page. The surface above it
// folds row <-> card now, so the anchor lives here and nowhere higher up —
// nesting an <a> inside an <a> is invalid and the browser un-nests it.
//
// It stays a real <a href> that opens in a new tab: ctrl-click and middle-click
// need no JS. A plain left click is handled by onOpenEvent (which opens the
// tab itself) and must stop propagation, or the surface would toggle on the
// same click.

import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { ScreenerEvent } from '../api/boardTypes';
import { eventHref } from '../board/routes';
import { NEW_TAB_LINK_PROPS } from '../../../../utils/newTab';

export const EVENT_IMAGE_TEST_ID = 'card-event-image';

export interface EventImageLinkProps {
    event: ScreenerEvent;
    size: number;
    /** CSS radius — the row and the card header round the slot differently. */
    radius: string;
    /** Fallback glyph size, tuned per slot. */
    fallbackFontSize: string;
    onOpenEvent?: (e: React.MouseEvent) => void;
}

export function EventImageLink({
    event,
    size,
    radius,
    fallbackFontSize,
    onOpenEvent,
}: EventImageLinkProps) {
    const theme = useTheme();
    const [failed, setFailed] = useState(false);
    const showImage = Boolean(event.image) && !failed;

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onOpenEvent?.(e);
    };

    return (
        <Box
            component="a"
            href={eventHref(event.event_id, event.slug)}
            {...NEW_TAB_LINK_PROPS}
            data-testid={EVENT_IMAGE_TEST_ID}
            aria-label={`Open ${event.title ?? 'event'}`}
            title="Open event"
            onClick={handleClick}
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: radius,
                overflow: 'hidden',
                bgcolor: alpha(theme.palette.text.primary, 0.01),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                // The only affordance saying the picture leaves the board while
                // the rest of the surface only folds. Cheap, and it has to be
                // legible at 26px.
                outline: '1px solid transparent',
                outlineOffset: 1,
                transition: 'outline-color 120ms ease',
                '&:hover': { outlineColor: alpha(theme.palette.primary.main, 0.9) },
                '&:focus-visible': { outlineColor: theme.palette.primary.main },
            }}
        >
            {showImage ? (
                <Box
                    component="img"
                    src={event.image as string}
                    alt=""
                    loading="lazy"
                    onError={() => setFailed(true)}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            ) : (
                <Typography
                    sx={{ fontSize: fallbackFontSize, fontWeight: 700, color: 'text.disabled' }}
                >
                    {(event.category ?? event.title ?? '?').charAt(0).toUpperCase()}
                </Typography>
            )}
        </Box>
    );
}